import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import MultiEmailSelect from "../../../components/ChangeSelect";
import {
  communityService,
  groupService,
  ServiceOnMount,
} from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import { GrGroup } from "react-icons/gr";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaPencilAlt,
  FaTrashAlt,
} from "react-icons/fa";
import LoadingImage from "../../../components/LoadingImage";
import { DataTable } from "../../../components/DataTable";
import Modal from "../../../components/Modal";
import useUserStore from "../../../stores/userStore";
import { toast } from "react-toastify";
import StatusBadge from "../../../components/table/StatusBadge"

const columns = [
  // {
  //   accessorKey: "groupId",
  //   header: "Group ID",
  //   cell: ({ row }) => (
  //     <span>
  //       <ShortenedText text={row.getValue("groupId")} />
  //     </span>
  //   ),
  // },
  {
    accessorKey: "groupName",
    header: "Group Name",
    cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
  },
  {
    accessorKey: "groupLeader",
    header: "Group Leader",
    cell: ({ row }) => <span>{row.getValue("groupLeader")}</span>,
  },
  {
    accessorKey: "totalMembers",
    header: "Total Members",
    cell: ({ row }) => <span>{row.getValue("totalMembers")}</span>,
  },
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => <span>{row.getValue("dateCreated")}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

const ViewCommunityPage = () => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("general");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToActivate, setGroupToActivate] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userRole, userId } = useUserStore();
  const [reload, setReload] = useState(false);

  const actions = (row) => [
    {
      label: "View/Edit Details",
      icon: <FaPencilAlt />,
      onClick: () => {
        navigate(`/group-management/edit/${row.groupId}`);
      },
      variant: "dropPlain",
    },
    {
      label: "Activate Group",
      icon: <FaCheckCircle />,
      onClick: () => {
        setGroupToActivate(row.groupId);
        setShowActivateModal(true);
      },
      variant: "dropGreen",
    },
    {
      label: "Deactivate Group",
      icon: <FaTrashAlt />,
      onClick: () => {
        setGroupToDelete(row.groupId);
        setShowDeleteModal(true);
      },
      variant: "dropRed",
    },
  ];

  const handleConfirmActivate = async () => {
    if (!groupToActivate) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToActivate, // assuming groupToDelete is just the group ID
        Status: "A",
      };

      await groupService.editGroup(payload);

      toast.success("🎉 Group activated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Activation failed:", error);
      toast.error("❌ Failed to activate group", {
        autoClose: 2000,
      });
    } finally {
      setShowActivateModal(false);
      setLoading(false);
      setGroupToActivate(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!groupToDelete) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToDelete, // assuming groupToDelete is just the group ID
        Status: "D",
      };

      await groupService.editGroup(payload);

      toast.success("🎉 Group has been deactivated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Deactivation failed:", error);
      toast.error("❌ Failed to deactivate group", {
        autoClose: 2000,
      });
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
      setGroupToDelete(null);
    }
  };

  const dataMap = (item) => ({
    communityID: item._id,
    name: item.name,
    status: item.status === "A" ? "Active" : "Dormant",
    adminID: item.admin_id,
    adminName: item.admin_name,
    adminEmails:
      item.admins
        .map((admin) => admin.entity?.email)
        .filter(Boolean) // Filter out any undefined or null emails
        .join(", ") || "Not Available", // Join emails with a comma, or return "Not Available" if no emails
    description: item.description,
    createdDate: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "Not Available",
    // Add address fields
    address_line1: item.address_line1 || "Not Available",
    address_line2: item.address_line2 || "Not Available",
    postcode: item.postcode || "Not Available",
    city: item.city || "Not Available",
    state: item.state || "Not Available",
    country: item.country || "Not Available",
  });

  const getCommunityDetails = ServiceOnMount(
    () => communityService.getCommunityDetails(communityId),
    dataMap
  );
  const communityData = getCommunityDetails.data?.[0];

  const groupMap = (item) => ({
    groupId: item._id,
    groupName: item.name,
    communityName: item.community?.name || "N/A",
    communityId: item.community?._id || "N/A",
    status:
      item?.status === "A"
        ? "Active"
        : item?.status === "D"
        ? "Deactivated"
        : item?.status,
    groupLeader:
      item.leaders && item.leaders.length > 0
        ? item.leaders
            .map((leader) => leader.profile?.name)
            .filter(Boolean) // remove any undefined/null names
            .join(", ")
        : "N/A",
    totalMembers: item.total,
    dateCreated: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "N/A",
  });
  const getGroupDetails = ServiceOnMount(
    () => groupService.getGroups(communityId),
    groupMap,
    [reload]
  );

  const groupData = getGroupDetails.data;

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleEdit = () => {
    navigate(`/communities/edit/${communityId}`);
  };

  useEffect(() => {
    if (getCommunityDetails.data?.length > 0) {
      const fetchedData = getCommunityDetails.data[0];
      navigate(location.pathname, {
        state: { communityName: fetchedData.name || "" },
        replace: true,
      });
    }
  }, [getCommunityDetails.data, navigate, location.pathname]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 sm:p-6 pb-1 rounded-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
              {/* Left Side: Icon + Title + Date */}
              <div className="flex gap-4 items-start sm:items-center">
                <div className="bg-teal-100 p-3 sm:p-4 rounded-full">
                  <GrGroup className="w-10 h-10 sm:w-16 sm:h-16 text-teal-700" />
                </div>

                <div>
                  <h1 className="text-base sm:text-lg font-semibold text-[#111827]">
                    {communityData ? (
                      communityData.name
                    ) : (
                      <Skeleton className="w-40 h-6" />
                    )}
                  </h1>
                  <div className="mt-4 sm:mt-2">
                    <div className="text-sm font-semibold text-[#6B7280]">
                      Created Date
                    </div>
                    <div className="text-sm text-[#111827]">
                      {communityData ? (
                        communityData.createdDate
                      ) : (
                        <Skeleton className="w-24 h-4" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side: Button */}
              <div className="flex w-full sm:w-auto flex-col sm:flex-row gap-2 sm:gap-4">
                <Button
                  onClick={handleEdit}
                  disabled={!communityData}
                  className="w-full sm:w-auto"
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>

          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>

            <button
              onClick={() => handleTabSwitch("address")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "address"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              Address Info
            </button>

            <button
              onClick={() => handleTabSwitch("groups")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "groups"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              Groups
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 sm:p-6 mt-0 bg-white rounded-r-lg rounded-b-lg">
            {activeTab === "general" ? (
              <>
                <div>
                  <Label>Status</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {communityData ? (
                      communityData.status
                    ) : (
                      <Skeleton className="w-32 h-4" />
                    )}
                  </p>
                </div>

                <div>
                  <Label>Community Admins</Label>
                  {communityData ? (
                    <MultiEmailSelect
                      selectedEmails={
                        communityData
                          ? communityData.adminEmails.split(", ")
                          : []
                      }
                      options={[]}
                      onChange={() => {}}
                      isDisabled={true}
                      isMulti={true}
                      showRemoveIcon={false}
                    />
                  ) : (
                    <Skeleton className="w-40 h-4" />
                  )}
                </div>

                <div>
                  <Label>Community Name</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {communityData ? (
                      communityData.name
                    ) : (
                      <Skeleton className="w-40 h-4" />
                    )}
                  </p>
                </div>

                <div>
                  <Label>Description</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {communityData ? (
                      communityData.description
                    ) : (
                      <Skeleton className="w-60 h-4" />
                    )}
                  </p>
                </div>
              </>
            ) : activeTab === "address" ? (
              <>
                {[
                  "address_line1",
                  "address_line2",
                  "postcode",
                  "city",
                  "state",
                  "country",
                ].map((field) => (
                  <div key={field}>
                    <Label>
                      {field
                        .replace("_", " ")
                        .replace("line1", "Line 1")
                        .replace("line2", "Line 2")
                        .toUpperCase()}
                    </Label>
                    <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                      {communityData ? (
                        communityData[field] || "Not Available"
                      ) : (
                        <Skeleton className="w-40 h-4" />
                      )}
                    </p>
                  </div>
                ))}
              </>
            ) : (
              // Groups Tab Content
              <div className="overflow-x-auto col-span-2 w-full md:max-w-[calc(100vw-375px)]">
                {getGroupDetails.loading ? (
                  // {!page.groupsTable.length ? (
                  <LoadingImage />
                ) : groupData.length > 0 ? (
                  // ) : page.groupsTable.length ? (
                  <DataTable
                    columns={columns}
                    data={groupData}
                    // data={page.groupsTable}
                    actions={actions}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-6">
                    No requests found.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Activate Modal */}
      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activate Group"
        description="Are you sure you want to Activate this group?"
        icon={<FaExclamationTriangle className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowActivateModal(false)}
            >
              No, Cancel
            </Button>
            <Button onClick={handleConfirmActivate} disabled={loading}>
              {loading ? "Activating" : "Yes Activate this group"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Deactivate Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Deactivate Group"
        description="Are you sure you want to deactivate this group?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDeactivate}
              disabled={loading}
            >
              {loading ? "Deactivating" : "Yes Deactivate this group"}
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default ViewCommunityPage;
